import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { T } from '@design/index';
import { patientApi } from '../../api';
import type { MarketplaceProvider, ProviderPricing } from '../../types';

const MODALITY_CHIPS = ['X-Ray', 'CT Scan', 'MRI', 'Ultrasound', 'ECG', 'Mammography', 'PET Scan', 'DEXA', 'Doppler', '2D Echo'];

function modalitiesOf(p: MarketplaceProvider): string[] {
  return p.tags.filter((t) => MODALITY_CHIPS.includes(t));
}

function hasTag(p: MarketplaceProvider, tag: string): boolean {
  return p.tags.some((t) => t.toLowerCase() === tag.toLowerCase());
}

function parsePricing(raw: ProviderPricing[] | string | undefined): ProviderPricing[] {
  if (!raw) return [];
  if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return []; } }
  return raw;
}

// ─── Generic modality preparation guidance — objective clinical info, not
// specific to any one center's real data ────────────────────────────────────

const PREP_NOTES: Record<string, string> = {
  'X-Ray': 'Remove metal accessories and jewellery before the scan. No special preparation required.',
  'CT Scan': 'Avoid eating or drinking for 4 hours before the scan if contrast is used. Inform the team about any allergies.',
  'MRI': 'Remove all metal objects. Inform the team if you have a pacemaker, implant, or claustrophobia. Avoid eating for 4 hours if abdominal MRI.',
  'Ultrasound': 'Drink 4–6 glasses of water 1 hour before the scan and do not urinate (for pelvic USG). Fast for 8 hours for abdominal USG.',
  'ECG': 'No special preparation. Avoid creams or lotions on the chest area.',
  'Mammography': 'Schedule the exam within the first two weeks of your menstrual cycle. Do not apply deodorant or lotion on the day.',
  'Doppler': 'Wear loose clothing. No special preparation required.',
  '2D Echo': 'No special preparation required. Wear a two-piece outfit for easy access.',
  'PET Scan': 'Fast for at least 6 hours. Avoid strenuous physical activity for 24 hours before the scan.',
  'DEXA': 'Avoid calcium supplements for 24 hours before. No special preparation required.',
};

// ─── Icons ───────────────────────────────────────────────────────────────────

function IconBack({ color }: { color: string }) {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
    </svg>
  );
}

function IconStar({ color }: { color: string }) {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="1.5">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function IconLocation({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function IconShield({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l8 4v6c0 5-3.5 9.7-8 11-4.5-1.3-8-6-8-11V6l8-4z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function IconInfo({ color }: { color: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" />
    </svg>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ImagingCenter() {
  const navigate = useNavigate();
  const { centerId } = useParams<{ centerId: string }>();
  const [activeModality, setActiveModality] = useState('All');
  const [center, setCenter] = useState<MarketplaceProvider | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!centerId) return;
    patientApi.getMarketplaceProvider(centerId)
      .then((data) => { if (alive) setCenter(data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [centerId]);

  const modalities = useMemo(() => (center ? modalitiesOf(center) : []), [center]);
  const availableModalities = useMemo(() => ['All', ...modalities], [modalities]);
  const pricing = useMemo(() => (center ? parsePricing(center.pricing) : []), [center]);
  const filteredStudies = useMemo(() => {
    if (activeModality === 'All') return pricing;
    // Pricing entries don't carry a structured modality field — match it
    // against the service name as a best effort.
    return pricing.filter((s) => s.service.toLowerCase().includes(activeModality.toLowerCase()));
  }, [pricing, activeModality]);

  if (loading) {
    return (
      <div style={{ background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, paddingBottom: 100 }}>
        <p style={{ fontFamily: T.font, fontSize: 13, color: T.textSec }}>Loading center…</p>
      </div>
    );
  }

  if (!center) {
    return (
      <div style={{ background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, paddingBottom: 100 }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontFamily: T.font, fontSize: 14, color: '#B91C1C', marginBottom: 16 }}>
            Imaging center not found.
          </p>
          <button
            onClick={() => navigate('/patient/care-services/imaging')}
            style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.white, background: '#4F46E5', border: 'none', borderRadius: 10, padding: '10px 20px', cursor: 'pointer' }}
          >
            Back to Imaging
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: T.bg, fontFamily: T.font, maxWidth: 1080, margin: '0 auto', paddingBottom: 100 }}>
      {/* Hero */}
      <div
        style={{
          background: `linear-gradient(135deg, #4F46E5 0%, #3730A3 100%)`,
          padding: '20px 16px 32px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', top: -30, right: -30, width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <button
            onClick={() => navigate('/patient/care-services/imaging')}
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <IconBack color={T.white} />
            <span style={{ fontFamily: T.font, fontSize: 11, fontWeight: 600, color: T.white }}>Back</span>
          </button>
          <button
            onClick={() => {
              const text = `${center.name}\n${center.address || ''}\nModalities: ${modalities.join(', ')}\n\nViewed on NeoHealthCard`;
              if (navigator.share) { navigator.share({ title: center.name, text }).catch(() => {}); }
              else { navigator.clipboard.writeText(text).then(() => alert('Center details copied to clipboard')).catch(() => {}); }
            }}
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke={T.white} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.59 13.51l6.83 3.98"/><path d="M15.41 6.51l-6.82 3.98"/>
            </svg>
            <span style={{ fontFamily: T.font, fontSize: 11, fontWeight: 600, color: T.white }}>Share</span>
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke={T.white} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <circle cx="12" cy="12" r="4" />
              <circle cx="12" cy="12" r="1.5" fill={T.white} stroke="none" />
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ fontFamily: T.font, fontSize: 18, fontWeight: 800, color: T.white, margin: '0 0 4px', lineHeight: 1.3 }}>
              {center.name}
            </h1>
            {center.address && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: T.font, fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
                <IconLocation color="rgba(255,255,255,0.6)" />
                {center.address}
              </div>
            )}
          </div>
        </div>

        {/* Rating & badges */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {center.rating > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '4px 10px' }}>
              <IconStar color="#FCD34D" />
              <span style={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: T.white }}>
                {center.rating.toFixed(1)}
              </span>
            </div>
          )}
          {hasTag(center, 'Emergency') && (
            <span style={{ fontFamily: T.font, fontSize: 10, fontWeight: 700, color: '#FCA5A5', background: 'rgba(239,68,68,0.2)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 20, padding: '3px 10px' }}>
              ⚡ Emergency Available
            </span>
          )}
          {hasTag(center, 'Home Portable') && (
            <span style={{ fontFamily: T.font, fontSize: 10, fontWeight: 700, color: '#6EE7B7', background: 'rgba(5,150,105,0.2)', border: '1px solid rgba(5,150,105,0.3)', borderRadius: 20, padding: '3px 10px' }}>
              🏠 Home Portable
            </span>
          )}
        </div>
      </div>

      {/* Contact & Hours */}
      {(center.phone || center.email || center.openHours) && (
        <div style={{ padding: '16px 16px 0' }}>
          <div
            style={{
              background: T.white,
              border: `1px solid ${T.border}`,
              borderRadius: 14,
              padding: 14,
              boxShadow: T.shadow,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {center.phone && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                </svg>
                <span style={{ fontFamily: T.font, fontSize: 12, color: T.text, fontWeight: 600 }}>{center.phone}</span>
              </div>
            )}
            {center.email && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><path d="M22 6l-10 7L2 6" />
                </svg>
                <span style={{ fontFamily: T.font, fontSize: 12, color: T.text, fontWeight: 600 }}>{center.email}</span>
              </div>
            )}
            {center.openHours && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
                </svg>
                <span style={{ fontFamily: T.font, fontSize: 12, color: T.text, fontWeight: 600 }}>{center.openHours}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modalities section */}
      {modalities.length > 0 && (
        <div style={{ padding: '16px 16px 0' }}>
          <h2 style={{ fontFamily: T.font, fontSize: 15, fontWeight: 800, color: T.text, margin: '0 0 10px' }}>
            Available Modalities
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {modalities.map((mod) => (
              <span
                key={mod}
                style={{
                  fontFamily: T.font,
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#4F46E5',
                  background: '#4F46E510',
                  border: '1px solid #4F46E520',
                  borderRadius: 20,
                  padding: '4px 10px',
                }}
              >
                {mod}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Studies */}
      <div style={{ padding: '20px 16px 0' }}>
        <h2 style={{ fontFamily: T.font, fontSize: 15, fontWeight: 800, color: T.text, margin: '0 0 10px' }}>
          Studies & Scans
        </h2>

        {modalities.length > 0 && (
          <div style={{ overflowX: 'auto', marginBottom: 14 }}>
            <div style={{ display: 'flex', gap: 8, width: 'max-content', paddingBottom: 2 }}>
              {availableModalities.map((mod) => {
                const active = activeModality === mod;
                return (
                  <button
                    key={mod}
                    onClick={() => setActiveModality(mod)}
                    style={{
                      fontFamily: T.font,
                      fontSize: 11,
                      fontWeight: 600,
                      color: active ? T.white : T.textSec,
                      background: active ? '#4F46E5' : T.white,
                      border: `1px solid ${active ? '#4F46E5' : T.border}`,
                      borderRadius: 20,
                      padding: '6px 12px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      boxShadow: T.shadow,
                      transition: 'background 0.15s, color 0.15s',
                    }}
                  >
                    {mod}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {filteredStudies.length === 0 ? (
          <p style={{ fontFamily: T.font, fontSize: 13, color: T.textSec, textAlign: 'center', padding: '24px 0' }}>
            This center hasn't listed pricing for studies yet.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filteredStudies.map((study, i) => (
              <div
                key={`${study.service}-${i}`}
                style={{
                  background: T.white,
                  border: `1px solid ${T.border}`,
                  borderRadius: 14,
                  padding: '14px 14px',
                  boxShadow: T.shadow,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontFamily: T.font, fontSize: 13, fontWeight: 700, color: T.text, margin: '0 0 4px' }}>
                      {study.service}
                    </p>
                    {study.note && (
                      <div style={{ fontFamily: T.font, fontSize: 10, color: T.textSec, marginBottom: 4 }}>{study.note}</div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <p style={{ fontFamily: T.font, fontSize: 15, fontWeight: 800, color: '#4F46E5', margin: '0 0 6px' }}>
                      ₹{study.price.toLocaleString()}
                    </p>
                    {center.phone ? (
                      <a
                        href={`tel:${center.phone}`}
                        style={{
                          display: 'inline-block',
                          fontFamily: T.font,
                          fontSize: 11,
                          fontWeight: 700,
                          color: T.white,
                          background: '#4F46E5',
                          borderRadius: 8,
                          padding: '6px 12px',
                          textDecoration: 'none',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Call centre
                      </a>
                    ) : (
                      <span style={{ fontFamily: T.font, fontSize: 10, color: T.textTer, whiteSpace: 'nowrap' }}>
                        No number listed
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preparation notes */}
      {modalities.length > 0 && (
        <div style={{ padding: '24px 16px 0' }}>
          <h2 style={{ fontFamily: T.font, fontSize: 15, fontWeight: 800, color: T.text, margin: '0 0 4px' }}>
            General Preparation Guidance
          </h2>
          <p style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, margin: '0 0 12px' }}>
            These are general pointers, not this centre's own instructions — always confirm preparation with the imaging centre before your visit.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {modalities.map((mod) =>
              PREP_NOTES[mod] ? (
                <div
                  key={mod}
                  style={{
                    background: T.white,
                    border: `1px solid ${T.border}`,
                    borderRadius: 12,
                    padding: '12px 14px',
                    display: 'flex',
                    gap: 10,
                    alignItems: 'flex-start',
                  }}
                >
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 8,
                      background: '#4F46E510',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <IconInfo color="#4F46E5" />
                  </div>
                  <div>
                    <p style={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: T.text, margin: '0 0 3px' }}>
                      {mod}
                    </p>
                    <p style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, margin: 0, lineHeight: 1.6 }}>
                      {PREP_NOTES[mod]}
                    </p>
                  </div>
                </div>
              ) : null
            )}
          </div>
        </div>
      )}

      {/* Safety note */}
      <div style={{ padding: '20px 16px 0' }}>
        <div
          style={{
            background: '#EFF6FF',
            border: '1px solid #BFDBFE',
            borderRadius: 12,
            padding: '14px 16px',
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
          }}
        >
          <div style={{ flexShrink: 0, marginTop: 1 }}>
            <IconShield color="#3B82F6" />
          </div>
          <div>
            <p style={{ fontFamily: T.font, fontSize: 12, fontWeight: 700, color: '#1E3A8A', margin: '0 0 3px' }}>
              Radiation Safety
            </p>
            <p style={{ fontFamily: T.font, fontSize: 11, color: '#1D4ED8', margin: 0, lineHeight: 1.6 }}>
              Ask the centre about its radiation-safety accreditation and radiologist credentials — NeoHealthCard does not certify these. Always inform the technician if you are pregnant or breastfeeding.
            </p>
          </div>
        </div>
      </div>

      {/* NeoHealthCard branding */}
      <div style={{ padding: '20px 16px 8px', textAlign: 'center' }}>
        <span style={{ fontFamily: T.font, fontSize: 9, color: T.textTer, lineHeight: 1.6 }}>
          NeoHealthCard™ · Verified imaging centre · Scans are ordered by your doctor or hospital, not booked here
        </span>
      </div>
    </div>
  );
}
